/**
 * BsSelect — every `<v-select>` in the app renders through this.
 *
 * Design System v3.0.0 makes the BuiltSimple dropdown the standard: "Over 10
 * options, a search box appears at the top" of the panel. VSelect has no such
 * box and there are ~390 v-selects, so rather than edit each one this wrapper
 * is registered globally under the name `VSelect` (components.ts), and
 * vite.config.mts tells vite-plugin-vuetify NOT to auto-import VSelect, so the
 * template tag `<v-select>` resolves to this component at runtime.
 *
 * It is deliberately transparent: every attr, listener and slot goes straight
 * to Vuetify's real VSelect (imported directly below, so this does not recurse).
 * The only thing it adds is, when the list has more than SEARCH_THRESHOLD
 * options and the caller did not fill `prepend-item` itself, a search box in
 * that slot that narrows the `items` handed down. The chosen option(s) are
 * always kept in the narrowed list, so the field never loses the title of the
 * current value while filtering.
 *
 * The look (one line per option, current one bold with a blue tick, same panel
 * as the nav menus) is CSS in scss/components/_ds-menu.scss and applies to
 * v-autocomplete / v-combobox as well; those already have their own search.
 *
 * Keyboard: the box takes focus when the panel opens (except on touch, where
 * that would pop the keyboard). Typing filters, Down moves into the options
 * (VList's own arrow handling), Enter picks the first match, Esc closes.
 *
 * Opt out per field with `no-search`.
 */
import { computed, defineComponent, h, nextTick, ref } from 'vue';
import type { PropType } from 'vue';
import { VSelect } from 'vuetify/components/VSelect';

import { filterItems, SEARCH_THRESHOLD } from './bsSelectFilter';

export { filterItems, propertyOf, SEARCH_THRESHOLD } from './bsSelectFilter';

type AnyRecord = Record<string, any>;

function attrOf(attrs: AnyRecord, camel: string, kebab: string): any {
    return attrs[camel] !== undefined ? attrs[camel] : attrs[kebab];
}

function isOn(v: any): boolean {
    return v === '' || (v !== undefined && v !== null && v !== false && v !== 'false');
}

export default defineComponent({
    name: 'BsSelect',
    inheritAttrs: false,
    props: {
        items: { type: Array as PropType<any[]>, default: undefined },
        noSearch: { type: Boolean, default: false }
    },
    setup(props, { attrs, slots }) {
        const query = ref('');
        const input = ref<HTMLInputElement | null>(null);

        const searchable = computed(
            () => !props.noSearch && !slots['prepend-item'] && Array.isArray(props.items) && props.items.length > SEARCH_THRESHOLD
        );

        const shownItems = computed(() => {
            if (!searchable.value || !query.value) return props.items;
            return filterItems(props.items as any[], query.value, {
                itemTitle: attrOf(attrs, 'itemTitle', 'item-title'),
                itemValue: attrOf(attrs, 'itemValue', 'item-value'),
                returnObject: isOn(attrOf(attrs, 'returnObject', 'return-object')),
                modelValue: attrOf(attrs, 'modelValue', 'model-value')
            });
        });

        function onMenu(open: boolean) {
            if (!searchable.value) return;
            if (open) {
                query.value = '';
                const coarse = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
                if (!coarse) {
                    nextTick(() => requestAnimationFrame(() => input.value?.focus({ preventScroll: true })));
                }
            } else {
                query.value = '';
            }
        }

        function onSearchKeydown(e: KeyboardEvent) {
            if (e.key === 'Home' || e.key === 'End' || e.key === ' ') {
                // Caret movement / typing a space, not VList's jump-to-first/last.
                e.stopPropagation();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                const list = (e.target as HTMLElement).closest('.v-list');
                const first = list?.querySelector<HTMLElement>('.v-list-item:not(.v-list-item--disabled)');
                first?.click();
            }
        }

        function renderSearch() {
            return h(
                'div',
                {
                    class: 'bs-select-search',
                    // VSelect's list prevents mousedown so the field keeps focus;
                    // the box has to be allowed to take it.
                    onMousedown: (e: MouseEvent) => e.stopPropagation(),
                    onClick: (e: MouseEvent) => e.stopPropagation()
                },
                [
                    h('i', { class: 'mdi mdi-magnify bs-select-search__icon', 'aria-hidden': 'true' }),
                    h('input', {
                        ref: input,
                        type: 'text',
                        class: 'bs-select-search__input',
                        value: query.value,
                        placeholder: 'Search',
                        'aria-label': 'Search options',
                        autocomplete: 'off',
                        onInput: (e: Event) => (query.value = (e.target as HTMLInputElement).value),
                        onKeydown: onSearchKeydown
                    })
                ]
            );
        }

        return () => {
            if (!searchable.value) {
                return h(VSelect as any, { ...attrs, items: props.items }, slots);
            }
            const userOnMenu = attrs['onUpdate:menu'];
            return h(
                VSelect as any,
                {
                    ...attrs,
                    items: shownItems.value,
                    'onUpdate:menu': [onMenu, ...(Array.isArray(userOnMenu) ? userOnMenu : userOnMenu ? [userOnMenu] : [])]
                },
                { ...slots, 'prepend-item': renderSearch }
            );
        };
    }
});
